/**
 * Public "Edit this template" designer (Certifier's edit-then-signup flow).
 *
 * Reached at /designer?template=<id> — typically from a badge-template landing
 * page's "Edit this template" CTA. Anyone (logged in or not) can customise the
 * badge and download a PNG/SVG. Saving/issuing requires an account: the design
 * is stashed and the user is sent to sign-up, then the create-badge modal
 * rehydrates it (see BadgesPage pickup of `bws:pendingBadgeDesign`).
 */
import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BadgeDesigner, type BadgeDesignerHandle } from '../components/BadgeDesigner';
import { LotusLogo } from '../components/LotusLogo';
import { useAuth } from '../hooks/useAuth';
import { TEMPLATES } from '../components/badge-designer-utils';

export const PENDING_DESIGN_KEY = 'bws:pendingBadgeDesign';

export function PublicDesignerPage() {
  const { t } = useTranslation('dashboard');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const designerRef = useRef<BadgeDesignerHandle>(null);
  const [busy, setBusy] = useState(false);

  const templateId = params.get('template') || undefined;
  const known = templateId && TEMPLATES.some((tpl) => tpl.id === templateId);
  const initialTemplateId = known ? templateId : undefined;

  const download = async (kind: 'png' | 'svg') => {
    const h = designerRef.current;
    if (!h) return;
    const data = kind === 'png' ? await h.onExport() : await h.onExportSVG();
    const href = kind === 'svg' ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(data)}` : data;
    const a = document.createElement('a');
    a.href = href;
    a.download = `badge.${kind}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const save = async () => {
    const h = designerRef.current;
    if (!h) return;
    setBusy(true);
    try {
      const [image, designJson] = [await h.onExport(), h.onExportJSON()];
      localStorage.setItem(PENDING_DESIGN_KEY, JSON.stringify({ image, designJson }));
    } catch { /* still route the user onward */ }
    setBusy(false);
    navigate(isAuthenticated ? '/badges' : '/signup');
  };

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      <header className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <LotusLogo className="h-7 w-7" />
          <span className="text-base font-bold text-gray-900">badges.ninja</span>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => download('png')}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('designer.downloadPng', 'Download PNG')}
          </button>
          <button
            onClick={() => download('svg')}
            className="hidden rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:inline-block"
          >
            {t('designer.downloadSvg', 'Download SVG')}
          </button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="public-designer-save"
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {isAuthenticated ? t('designer.saveBadge', 'Save badge') : t('designer.signUpToSave', 'Sign up to save')}
          </button>
        </div>
      </header>

      <main className="min-h-0 flex-1">
        <BadgeDesigner
          ref={designerRef}
          initialTemplateId={initialTemplateId}
          autoOpenTemplates={!initialTemplateId}
        />
      </main>
    </div>
  );
}
